import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WorkflowType } from '@ca/core/models';
import { Guid } from 'guid-typescript';
import { Observable } from 'rxjs';
import { ArchivedWorkflowRunResult } from '../models/archived-workflow-run.model';
import { StepInput } from '../models/run-step.model';
import { WorkflowRunResponse } from '../models/workflow-run-response.model';
import { WorkflowRunStatusResult } from '../models/workflow-run-status.model';
import { WorkflowRunStepStatus } from '../models/workflow-run-step-status.model';
import { TrainingMethod } from '../types/training-method.type';

@Injectable({
    providedIn: 'root',
})
export class WorkflowRunApiService {
    private readonly apiUrl = '/api/workflow_run/workflowruns';
    private readonly headers = { 'Content-Type': 'application/json' };

    constructor(private http: HttpClient) {}

    startWorkflowRun(
        workflowId: string,
        inputs: StepInput[],
        stepByStepFlowIds: string[],
        workflowRunName?: string,
        classes?: string[],
        classesOverlap?: boolean,
        trainingType?: TrainingMethod,
        trainingResultId?: string,
        notifyWhenIsDone: boolean = false,
        zenCompatible: boolean = false,
        datasetId: string = Guid.EMPTY.toString(),
        workflowType: WorkflowType = WorkflowType.UNDEFINED,
        modelId?: string,
        trainingId?: string,
        datasetVersionId?: string,
    ): Observable<WorkflowRunResponse> {
        return this.http.post<WorkflowRunResponse>(this.apiUrl, {
            workflowId: workflowId,
            datasetId,
            inputs,
            stepByStepFlowIds,
            workflowRunName,
            notifyWhenIsDone,
            trainingResultId,
            trainingType,
            classes,
            classesOverlap,
            zenCompatible,
            mlModelId: modelId,
            workflowType,
            continueTrainingId: trainingId,
            datasetVersionId,
        });
    }

    getWorkflowLog(workflowRunId: string): Observable<string> {
        return this.http.get(`${this.apiUrl}/${workflowRunId}/logs`, { responseType: 'text' });
    }

    getWorkflowStepLog(workflowRunId: string, moduleId: number, stepId: number, iterationId?: number): Observable<string> {
        let requestUri = `${this.apiUrl}/${workflowRunId}/logs/${moduleId}/${stepId}`;
        if (iterationId != null) {
            requestUri = requestUri + `/${iterationId}`;
        }
        return this.http.get(requestUri, { responseType: 'text' });
    }

    getWorkflowRunStatus(id: string): Observable<WorkflowRunStatusResult> {
        return this.http.get<WorkflowRunStatusResult>(`${this.apiUrl}/${id}`, { headers: this.headers });
    }

    getArchivedWorkflowRun(id: string): Observable<ArchivedWorkflowRunResult> {
        return this.http.get<ArchivedWorkflowRunResult>(`${this.apiUrl}/archive/${id}`, { headers: this.headers });
    }

    triggerReRun(workflowRunId: string, inputs: StepInput[], stepByStepFlowIds: string[]): Observable<{ workflowRunId: string }> {
        return this.http.post<{ workflowRunId: string }>(`${this.apiUrl}/archive/${workflowRunId}/reruns`, {
            inputs,
            stepByStepFlowIds,
        });
    }

    completeModule(workflowId: string, stepId: number, moduleId: number): Observable<WorkflowRunStepStatus> {
        return this.http.post<WorkflowRunStepStatus>(
            `${this.apiUrl}/${workflowId}/step/${stepId}/module/${moduleId}`,
            {
                InteractiveWorkflowCommand: 'completeModule',
            },
            { headers: this.headers },
        );
    }

    executeModule(workflowId: string, stepId: number, moduleId: number, inputs: StepInput): Observable<WorkflowRunStepStatus> {
        return this.http.post<WorkflowRunStepStatus>(
            `${this.apiUrl}/${workflowId}/step/${stepId}/module/${moduleId}`,
            {
                InteractiveWorkflowCommand: 'executeModule',
                inputs,
            },
            { headers: this.headers },
        );
    }

    deleteRun(workflowRunId: string): Observable<Object> {
        return this.http.delete(`${this.apiUrl}/${workflowRunId}`);
    }
}
